<script setup lang="ts">
import { isFunction } from '@pureadmin/utils'
import ExitFullscreen from '~icons/ri/fullscreen-exit-fill'
import Fullscreen from '~icons/ri/fullscreen-fill'

import { computed, ref } from 'vue'

import { IconifyIconOffline } from '@/components/ReIcon'

import { type ButtonProps, closeDialog, type DialogOptions, dialogStore, type EventType } from './index'

defineOptions({
	name: 'ReDialog',
})

const btnLoadingMap = ref<Record<number, { loading: boolean }>>({})
const fullscreen = ref(false)

const footerButtons = computed(() => {
	return (options: DialogOptions) => {
		return options?.footerButtons?.length > 0
			? options.footerButtons
			: ([
					{
						label: '取消',
						text: true,
						bg: true,
						loading: false,
						btnClick: ({ dialog: { options, index } }) => {
							// 2025年7月9日改一个取消也关闭loading
							const closeLoading = () => {
								if (options?.sureBtnLoading && btnLoadingMap.value[index]) {
									btnLoadingMap.value[index].loading = false
								}
							}
							const done = () => {
								closeLoading()
								closeDialog(options, index, { command: 'sure' })
							}
							if (options?.beforeCancel && isFunction(options?.beforeCancel)) {
								options.beforeCancel(done, { options, index })
							} else {
								done()
							}
						},
					},
					{
						label: '确定',
						type: 'primary',
						text: true,
						bg: true,
						loading: true,
						popconfirm: options?.popconfirm,
						btnClick: ({ dialog: { options, index } }) => {
							// console.log(options.sureBtnLoading);
							if (options?.sureBtnLoading) {
								btnLoadingMap.value[index] = Object.assign({}, btnLoadingMap.value[index], {
									loading: true,
								})
							}
							const closeLoading = () => {
								if (options?.sureBtnLoading) {
									btnLoadingMap.value[index].loading = false
								}
							}
							const done = () => {
								closeLoading()
								closeDialog(options, index, { command: 'sure' })
							}
							if (options?.beforeSure && isFunction(options?.beforeSure)) {
								options.beforeSure(done, { options, index, closeLoading })
							} else {
								done()
							}
						},
					},
				] as Array<ButtonProps>)
	}
})

const fullscreenClass = computed(() => {
	return ['el-icon', 'el-dialog__close', '-translate-x-2', 'cursor-pointer', 'hover:text-[red]!']
})

function eventsCallBack(event: EventType, options: DialogOptions, index: number, isClickFullScreen = false) {
	if (!isClickFullScreen) fullscreen.value = options?.fullscreen ?? false
	if (options?.[event] && isFunction(options?.[event])) {
		return options?.[event]({ options, index })
	}
}

function handleClose(options: DialogOptions, index: number, args = { command: 'close' }) {
	closeDialog(options, index, args)
	eventsCallBack('close', options, index)
}
</script>

<template>
	<el-dialog
		v-for="(options, index) in dialogStore"
		:key="index"
		v-bind="options"
		v-model="options.visible"
		:show-close="options.showClose"
		class="pure-dialog"
		:fullscreen="fullscreen ? true : options?.fullscreen ? true : false"
		@closed="handleClose(options, index)"
		@opened="eventsCallBack('open', options, index)"
		@openAutoFocus="eventsCallBack('openAutoFocus', options, index)"
		@closeAutoFocus="eventsCallBack('closeAutoFocus', options, index)"
	>
		<!--    {{ options }}-->
		<!-- header -->
		<template v-if="options?.fullscreenIcon || options?.headerRenderer" #header="{ close, titleId, titleClass }">
			<div v-if="options?.fullscreenIcon" class="flex items-center justify-between">
				<span :id="titleId" :class="titleClass">{{ options?.title }}</span>
				<i
					v-if="!options?.fullscreen"
					:class="fullscreenClass"
					@click="
						() => {
							fullscreen = !fullscreen
							eventsCallBack('fullscreenCallBack', { ...options, fullscreen }, index, true)
						}
					"
				>
					<IconifyIconOffline class="pure-dialog-svg" :icon="fullscreen ? ExitFullscreen : Fullscreen" />
				</i>
			</div>
			<component :is="options?.headerRenderer({ close, titleId, titleClass })" v-else />
		</template>
		<component
			v-if="options?.contentRenderer && isFunction(options.contentRenderer)"
			v-bind="options?.props"
			:is="options.contentRenderer({ options, index })"
			v-loading="btnLoadingMap[index]?.loading && options?.sureContentLoading"
			@close="(args) => handleClose(options, index, args)"
		/>
		<div v-else class="p-4 text-center text-gray-500">请提供 contentRenderer 属性（必须是一个函数）</div>
		<!-- footer -->
		<template v-if="!options?.hideFooter" #footer>
			<template v-if="options?.footerRenderer">
				<component :is="options?.footerRenderer({ options, index })" />
			</template>
			<span v-else>
				<template v-for="(btn, key) in footerButtons(options)" :key="key">
					<el-popconfirm
						v-if="btn.popconfirm"
						v-bind="btn.popconfirm"
						@confirm="
							btn.btnClick({
								dialog: { options, index },
								button: { btn, index: key },
								btnLoadingMap,
							})
						"
					>
						<template #reference>
							<el-button v-bind="btn">{{ btn?.label }}</el-button>
						</template>
					</el-popconfirm>
					<el-button
						v-else
						v-bind="btn"
						:loading="(btn?.loading || false) && btnLoadingMap[index]?.loading"
						@click="
							btn.btnClick({
								dialog: { options, index },
								button: { btn, index: key },
								btnLoadingMap,
							})
						"
					>
						{{ btn?.label }}
					</el-button>
				</template>
			</span>
		</template>
	</el-dialog>
</template>
